import {
    Box,
    Text,
    Flex,
    IconButton,
    Tooltip,
    VStack,
    useBreakpointValue,
    HStack,
    Divider,
    Badge,
    Icon,
    SimpleGrid,
    Button
} from '@chakra-ui/react';
import { useNavigate } from 'react-router-dom';
import moment from 'moment';
import { FiEye, FiEdit2, FiArrowDown, FiArrowUp, FiCalendar, FiUsers, FiUser, FiInfo } from 'react-icons/fi';
import { useTranslation } from "react-i18next";

const EventsCardList = ({ events, sort, sortDir, onSort, loading, isSuperAdmin }) => {
    const navigate = useNavigate();
    const { t } = useTranslation("default");
    const isMobile = useBreakpointValue({ base: true, md: false });

    const getStatus = (event) => {
        const now = new Date();
        const start = new Date(`${event.startDate}T${event.startTime}`);
        return start > now ? t('pending') : t('completed');
    };

    const getStatusColor = (event) => {
        const now = new Date();
        const end = new Date(`${event.date}T${event.endTime}`);
        return end > now ? 'orange' : 'green';
    };

    const columns = [
        { key: 'id', label: t('event.card.table.th.id') },
        { key: 'title', label: t('event.card.table.th.title') },
        ...(isSuperAdmin ? [{ key: 'autor', label: t('event.card.table.th.author') }] : []),
        { key: 'status', label: t('event.card.table.th.status') },
        { key: 'date', label: t('event.card.table.th.date') },
        { key: 'participants', label: t('event.card.table.th.participants') },
        { key: 'created', label: t('event.card.table.th.created') },
        { key: 'actions', label: t('event.card.table.th.actions') },
    ];

    const renderSortIcon = (key) => {
        if (sort !== key) return null;
        return sortDir === 'asc' ? <FiArrowUp /> : <FiArrowDown />;
    };

    return (
        <Box w="100%" maxW="7xl" mx="auto">
            {!isMobile && (
                <Box
                    display="grid"
                    gridTemplateColumns={isSuperAdmin ? "50px repeat(7, minmax(0, 1fr))" : "50px repeat(6, minmax(0, 1fr))"}
                    gap="12px"
                    mb="8px"
                >
                    {columns.map(col => (
                        <Box
                            key={col.key}
                            bg="brand.500"
                            borderRadius="0px"
                            p="12px"
                            display="flex"
                            alignItems="center"
                            fontSize="14px"
                            fontWeight="500"
                            color="white"
                            cursor={col.key !== 'actions' ? 'pointer' : 'default'}
                            onClick={() => col.key !== 'actions' && onSort(col.key)}
                            boxShadow="sm"
                            overflow="hidden"
                        >
                            <Flex align="center" gap={1} overflow="hidden">
                                <Text isTruncated>{col.label}</Text>
                                {renderSortIcon(col.key)}
                            </Flex>
                        </Box>
                    ))}
                </Box>
            )}

            {!loading && events.length === 0 ? (
                <VStack align="center" gap={'48px'} p={'48px'}>
                    <Text fontFamily={'Poppins'} fontWeight={'500'} fontSize={'20px'} color="#363636">
                        {t('event.not_found')}
                    </Text>
                </VStack>
            ) : (
                events.map(event => (
                    <Box
                        key={event.id}
                        display="grid"
                        gridTemplateColumns={{
                            base: '1fr',
                            md: isSuperAdmin ? '50px repeat(7, minmax(0, 1fr))' : '50px repeat(6, minmax(0, 1fr))'
                        }}
                        gap={{ base: '0', md: '12px' }}
                        bg={{ base: 'white', md: 'transparent' }}
                        p={{ base: '0', md: '0' }}
                        mb="12px"
                        borderRadius={{ base: '12px', md: '0' }}
                        boxShadow={{ base: 'md', md: 'none' }}
                        border={{ base: '1px solid', md: 'none' }}
                        borderColor={{ base: 'gray.100', md: 'transparent' }}
                        overflow="hidden"
                    >
                        {isMobile ? (
                            /* Premium Mobile Card Layout */
                            <Box p="16px" w="100%">
                                <Flex justify="space-between" align="start" mb="12px">
                                    <VStack align="start" spacing={1} flex="1">
                                        <Text fontSize="xs" color="gray.500" fontWeight="bold">ID: {event.eventId}</Text>
                                            <Text fontSize="lg" fontWeight="700" color="#0A4725" wordBreak="normal" overflowWrap="anywhere">
                                                {event.title}
                                            </Text>
                                    </VStack>
                                    <Badge
                                        bg={getStatusColor(event)}
                                        color="white"
                                        borderRadius="full"
                                        px={3}
                                        py={1}
                                        fontSize="xs"
                                        textTransform="none"
                                    >
                                        {getStatus(event)}
                                    </Badge>
                                </Flex>

                                <Divider mb="12px" />

                                <SimpleGrid columns={{ base: 1, sm: 2 }} spacing={4} mb="16px">
                                    <HStack spacing={3}>
                                        <Icon as={FiCalendar} color="brand.500" />
                                        <VStack align="start" spacing={0}>
                                            <Text fontSize="xs" color="gray.500">{t('event.card.table.th.date')}</Text>
                                            <Text fontSize="sm" fontWeight="500">
                                                {moment(event.startDate + 'T' + event.startTime).format('D.MM.YYYY H:mm')}
                                            </Text>
                                        </VStack>
                                    </HStack>

                                    <HStack spacing={3}>
                                        <Icon as={FiUsers} color="brand.500" />
                                        <VStack align="start" spacing={0}>
                                            <Text fontSize="xs" color="gray.500">{t('event.card.table.th.participants')}</Text>
                                            <HStack spacing={1}>
                                                <Text fontSize="sm" fontWeight="500">
                                                    {event.participantsCount}/{event.maxParticipants}
                                                </Text>
                                                {event.participantsCount >= event.maxParticipants && (
                                                    <Icon as={FiInfo} color="red.500" boxSize={3} />
                                                )}
                                            </HStack>
                                        </VStack>
                                    </HStack>

                                    {isSuperAdmin && (
                                        <HStack spacing={3}>
                                            <Icon as={FiUser} color="brand.500" />
                                            <VStack align="start" spacing={0}>
                                                <Text fontSize="xs" color="gray.500">{t('event.card.table.th.author')}</Text>
                                                <Text fontSize="sm" fontWeight="500" isTruncated>
                                                    {event.authorFullName || '-'}
                                                </Text>
                                            </VStack>
                                        </HStack>
                                    )}

                                    <HStack spacing={3}>
                                        <Icon as={FiCalendar} color="gray.400" />
                                        <VStack align="start" spacing={0}>
                                            <Text fontSize="xs" color="gray.500">{t('event.card.table.th.created')}</Text>
                                            <Text fontSize="sm" fontWeight="500">
                                                {moment(event.createdAt).format('D.MM.YYYY H:mm')}
                                            </Text>
                                        </VStack>
                                    </HStack>
                                </SimpleGrid>

                                <Flex gap={3}>
                                    <Button
                                        leftIcon={<FiUsers />}
                                        flex="1"
                                        size="md"
                                        variant="outline"
                                        colorScheme="brand"
                                        borderColor="#0A4725"
                                        color="#0A4725"
                                        onClick={() => navigate(`/events/${event.id}/participants`)}
                                        _hover={{ bg: 'brand.50' }}
                                    >
                                        {t('event.participants.title')}
                                    </Button>
                                    <Button
                                        leftIcon={<FiEye />}
                                        flex="1"
                                        size="md"
                                        variant="outline"
                                        colorScheme="brand"
                                        borderColor="#0A4725"
                                        color="#0A4725"
                                        onClick={() => window.open(`/events/${event.id}/preview`, '_blank')}
                                        _hover={{ bg: 'brand.50' }}
                                    >
                                        {t('preview')}
                                    </Button>
                                    <Button
                                        leftIcon={<FiEdit2 />}
                                        flex="1"
                                        size="md"
                                        bg="#0A4725"
                                        color="white"
                                        onClick={() => navigate(`/events/${event.id}/edit`)}
                                        _hover={{ bg: '#083a1e' }}
                                    >
                                        {t('edit')}
                                    </Button>
                                </Flex>
                            </Box>
                        ) : (
                            /* Desktop Row Layout */
                            <>
                                {/* ID */}
                                <Box bg="white" p="12px" display="flex" alignItems="center" onClick={() => navigate(`/events/${event.id}/edit`)} cursor={"pointer"} overflow="visible">
                                    <Text wordBreak="normal" overflowWrap="anywhere">{event.eventId}</Text>
                                </Box>

                                {/* Title */}
                                <Tooltip label={event.title} hasArrow isDisabled={event.title.length < 20}>
                                    <Box bg="white" p="12px" display="flex" alignItems="center" onClick={() => navigate(`/events/${event.id}/edit`)} cursor={"pointer"} overflow="visible">
                                        <Text fontWeight="500" wordBreak="normal" overflowWrap="anywhere">{event.title}</Text>
                                    </Box>
                                </Tooltip>

                                {/* Author (SuperAdmin only) */}
                                {isSuperAdmin && (
                                    <Box bg="white" p="12px" display="flex" alignItems="center" overflow="visible">
                                        <Text wordBreak="normal" overflowWrap="anywhere">{event.authorFullName || '-'}</Text>
                                    </Box>
                                )}

                                {/* Status */}
                                <Box bg="white" p="12px" display="flex" alignItems="center" overflow="hidden">
                                    <Badge
                                        bg={getStatusColor(event)}
                                        borderRadius="4px"
                                        px={2}
                                        py={1}
                                        color="white"
                                        fontSize="12px"
                                        textTransform="none"
                                        fontWeight="500"
                                        w="auto"
                                    >
                                        {getStatus(event)}
                                    </Badge>
                                </Box>

                                {/* Start Date */}
                                <Box bg="white" p="12px" display="flex" alignItems="center" overflow="visible">
                                    <Text wordBreak="normal" overflowWrap="anywhere">{moment(event.startDate + 'T' + event.startTime).format('D.MM.YYYY H:mm')}</Text>
                                </Box>

                                {/* Participants */}
                                <Box bg="white" p="12px" display="flex" alignItems="center" overflow="visible">
                                    <Flex align="center" gap="8px" overflow="visible">
                                        <Text wordBreak="normal" overflowWrap="anywhere">
                                            {event.participantsCount}/{event.maxParticipants}
                                        </Text>
                                        {event.participantsCount >= event.maxParticipants && (
                                            <Icon as={FiInfo} color="red.500" boxSize={4} />
                                        )}
                                    </Flex>
                                </Box>

                                {/* Created At */}
                                <Box bg="white" p="12px" display="flex" alignItems="center" overflow="visible">
                                    <Text wordBreak="normal" overflowWrap="anywhere">{moment(event.createdAt).format('D.MM.YYYY H:mm')}</Text>
                                </Box>

                                {/* Actions */}
                                <Box bg="white" p="12px" display="flex" justifyContent="center" gap={2} alignItems="center">
                                    <Tooltip label={t('event.participants.title')}>
                                        <IconButton
                                            icon={<FiUsers />}
                                            aria-label={t('event.participants.title')}
                                            size="sm"
                                            onClick={() => navigate(`/events/${event.id}/participants`)}
                                            variant="ghost"
                                            color="#0A4725"
                                            _hover={{ bg: 'brand.50' }}
                                        />
                                    </Tooltip>
                                    <Tooltip label={t('preview')}>
                                        <IconButton
                                            icon={<FiEye />}
                                            aria-label={t('preview')}
                                            size="sm"
                                            onClick={() => window.open(`/events/${event.id}/preview`, '_blank')}
                                            variant="ghost"
                                            color="#0A4725"
                                            _hover={{ bg: 'brand.50' }}
                                        />
                                    </Tooltip>
                                    <Tooltip label={t('edit')}>
                                        <IconButton
                                            icon={<FiEdit2 />}
                                            aria-label={t('edit')}
                                            size="sm"
                                            onClick={() => navigate(`/events/${event.id}/edit`)}
                                            variant="ghost"
                                            color="#0A4725"
                                            _hover={{ bg: 'brand.50' }}
                                        />
                                    </Tooltip>
                                </Box>
                            </>
                        )}
                    </Box>
                ))
            )}
        </Box>
    );
};

export default EventsCardList;
